import { useId } from 'react'
import { Button } from '@mui/material'
import { MeApi } from '@ulysses-inc/harami_api_client'
import { fetchSession } from '~/adapter/api/deviceAuthSession'
import {
  getRequestError,
  isAppVersionInconsistentError,
} from '~/adapter/api/error'
import { useAuthApi } from '~/adapter/api/useAuthApi'
import { routes } from '~/app/routes/paths'
import { useAppVersionConsistency } from '~/stores/appVersionConsistency/appVersionConsistency'
import {
  selectKioskAuthError,
  useKioskAuthErrorStore,
  type KioskAuthError as KioskAuthErrorState,
} from '~/stores/kioskAuthError/kioskAuthError'
import { replaceLocation } from '~/utils/navigation'
import {
  KioskErrorContent,
  KioskErrorDialog,
  KioskRetryFailedAlert,
} from '../KioskErrorScreen/KioskErrorScreen'
import { useKioskRetry } from '../KioskErrorScreen/hooks/useKioskRetry'

// ② 端末アカウント設定が不足している場合の共通本文（Rump ガイドラインのひな形 A）
const SETUP_MISSING_PARAGRAPHS = [
  '端末を管理している社内のシステム管理者までご連絡ください。',
  '設定が完了した後に、以下の「再試行」を押すとご利用いただけます。',
] as const

const REPORT_SAVED_OFFLINE_PARAGRAPH =
  '編集中のレポートは端末に保存しました。設定が完了した後に、オフラインレポート一覧からアップロードしてください。'

const RETRY_FAILED_MESSAGE =
  '引き続きご利用いただけません。社内のシステム管理者へご連絡ください。'

const contentByKind = (
  kind: KioskAuthErrorState['kind'],
): { title: string; paragraphs: readonly string[] } => {
  switch (kind) {
    case 'device_setup':
      // ① 証明書および接続自体に問題がある場合。全サービス共通の固定文言（変更不可）
      return {
        title: '端末の設定が確認できないため、ご利用いただけません',
        paragraphs: [
          'カミナシのサービスを利用するための有効な証明書が確認できず、通信に失敗しています。',
          '端末を管理している社内のシステム管理者までご連絡ください。',
          '設定が完了した後に、以下の「再試行」を押すとご利用いただけます。',
        ],
      }
    case 'service_assignment_required':
      return {
        title: 'カミナシ レポートの利用権限が無いため、ご利用いただけません',
        paragraphs: SETUP_MISSING_PARAGRAPHS,
      }
    case 'invalid_audience':
      return {
        title: '端末アカウントの認証設定に問題があるため、ご利用いただけません',
        paragraphs: SETUP_MISSING_PARAGRAPHS,
      }
  }
}

/**
 * キオスク端末（kiosk）で認証・認可に失敗したときの全画面エラー。
 * GlobalLayout が useKioskAuthErrorStore を購読して表示する。
 *
 * キオスク端末アカウントには「ログイン」「ログアウト」の概念が無いため、/login へは遷移せず
 * その場に被せる。「再試行」は Device セッションの再確立（fetchSession）に続けて GET /me で
 * 利用可否を確かめ、成功したら / からやり直す（認証フローを最初から通す）。
 * IP アドレス制限で弾かれた場合は IpRestrictionError に表示を譲る。
 */
export function KioskAuthError() {
  const titleId = useId()
  const error = useKioskAuthErrorStore(selectKioskAuthError)
  const kioskAuthErrorResolved = useKioskAuthErrorStore(
    state => state.kioskAuthErrorResolved,
  )
  const { getApi } = useAuthApi()

  const { isRetrying, isRetryFailed, retry } = useKioskRetry(async () => {
    const session = await fetchSession()
    if (!session.success) {
      if (session.reason === 'ip_address_restricted') {
        // fetchSession 内で IP 制限フラグが立ち IpRestrictionError が表示されるので、
        // こちらは閉じて二重表示を避ける
        kioskAuthErrorResolved()
        return true
      }
      return false
    }
    try {
      // セッションは張れても利用権限（21）や audience（22）の問題は /me まで行かないと分からない
      await getApi(MeApi).getMe()
    } catch (e) {
      // ここは QueryClient を通らないため queryClient.ts の共通ハンドリングが効かない。
      // アプリのバージョン不整合だけは UpdateModal を出すために共通状態へ通知する
      if (isAppVersionInconsistentError(await getRequestError(e))) {
        useAppVersionConsistency.getState().inconsistentVersionDetected()
      }
      return false
    }
    replaceLocation(routes.home.path)
    return true
  })

  if (error === null) {
    return null
  }

  const content = contentByKind(error.kind)
  const paragraphs = error.reportSavedOffline
    ? [...content.paragraphs, REPORT_SAVED_OFFLINE_PARAGRAPH]
    : content.paragraphs

  return (
    <KioskErrorDialog titleId={titleId}>
      <KioskErrorContent
        titleId={titleId}
        title={content.title}
        paragraphs={paragraphs}
        alert={
          isRetryFailed && (
            <KioskRetryFailedAlert message={RETRY_FAILED_MESSAGE} />
          )
        }
        action={
          <Button
            onClick={retry}
            loading={isRetrying}
            variant="contained"
            size="large"
          >
            再試行
          </Button>
        }
      />
    </KioskErrorDialog>
  )
}
